/**
 * Botão "Ajuda desta tela" (D-062): abre, ao lado, o trecho do guia da tela, sem sair dela.
 * Esc fecha e o foco volta ao botão. A tecla ? abre a ajuda da tela atual (Layout).
 * Importa o Botao direto (e não o índice da ui) para não criar dependência circular com o cabeçalho.
 */
import { CircleHelp, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Botao } from '../../components/ui/Botao';
import { ConteudoSecao } from './Conteudo';
import { lerReferencia } from './guias';
import { ancoraSecao } from './tipos';

export function BotaoAjuda({ referencia }: { referencia: string }) {
  const [aberto, setAberto] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);
  const fechar = useRef<HTMLButtonElement>(null);
  const idTitulo = useId();
  const alvo = lerReferencia(referencia);

  useEffect(() => {
    if (!aberto) return;
    fechar.current?.focus();
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setAberto(false);
        botao.current?.focus();
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto]);

  if (!alvo) return null;
  const { guia, secao } = alvo;
  const encerrar = () => {
    setAberto(false);
    botao.current?.focus();
  };

  return (
    <>
      <Botao
        ref={botao}
        variante="fantasma"
        tamanho="pequeno"
        className="print:hidden"
        data-ajuda-botao=""
        aria-haspopup="dialog"
        aria-expanded={aberto}
        onClick={() => setAberto(true)}
      >
        <CircleHelp className="size-4" aria-hidden="true" />
        Ajuda desta tela
      </Botao>
      {aberto && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={encerrar} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTitulo}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col gap-4 overflow-y-auto border-l border-borda bg-superficie p-5 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium tracking-wide text-texto-suave uppercase">
                  Ajuda
                </p>
                <p id={idTitulo} className="text-lg font-semibold text-texto">
                  {guia.titulo}
                </p>
              </div>
              <Botao
                ref={fechar}
                variante="fantasma"
                tamanho="icone"
                aria-label="Fechar a ajuda"
                onClick={encerrar}
              >
                <X aria-hidden="true" />
              </Botao>
            </div>
            <ConteudoSecao guia={guia} secao={secao} Titulo="h2" />
            <Link
              to={`/ajuda/${guia.id}#${ancoraSecao(guia.id, secao.id)}`}
              className="text-sm font-medium text-primaria hover:underline"
              onClick={() => setAberto(false)}
            >
              Abrir o guia completo
            </Link>
          </div>
        </>
      )}
    </>
  );
}
